import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  createStyles,
  makeStyles,
  Theme,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import CardCommon from "../../../../components/card/CardCommon";
import WithLoading from "../../../../utils/WithLoading";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import CreateOfferModal from "./CreateOfferModal";
import EditOfferModal from "./EditOfferModal";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableHeader: {
      height: 40,
      display: "flex",
      alignItems: "center",
      fontWeight: 600,
    },
    tableRow: {
      minHeight: 40,
      display: "flex",
      alignItems: "center",
      marginTop: 2,
    },
    textLeft: {
      display: "flex",
      justifyContent: "flex-start",
      paddingLeft: 12,
    },
    textRight: {
      display: "flex",
      justifyContent: "flex-end",
      paddingRight: 12,
    },
  }),
);

const offerTypeMap: Record<string, string> = {
  BUY_X_GET_Y_CHEAPEST_DISCOUNTED: "Buy X Get Y Discounted",
  FIXED_PRICE_FOR_X_ITEMS: "Fixed Price for X Items",
  FIXED_PRICE_FROM_X_LISTS: "Meal Deal (Pick from Lists)",
};

interface OfferNode {
  id: string;
  name: string;
  description: string;
  offerType: string;
  isActive: boolean;
  [key: string]: any;
}

interface OfferTypesProps {
  nodes: OfferNode[];
  getOfferInfo: () => void;
  isLoading: boolean;
  isLoadingButton: boolean;
  offerItemMap: any;
  setOfferItemMap: any;
}

const OfferTypes: React.FC<OfferTypesProps> = ({
  nodes,
  getOfferInfo,
  isLoading,
  isLoadingButton,
  offerItemMap,
  setOfferItemMap,
}) => {
  const classes = useStyles();

  const [offers, setOffers] = useState<OfferNode[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<OfferNode | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    setOffers(nodes || []);
  }, [nodes]);

  const openEditModal = (offer: OfferNode) => {
    setSelectedOffer(offer);
    setIsEditModalOpen(true);
  };

  const openCreateModal = () => setIsCreateModalOpen(true);

  return (
    <>
      {/* Header Button */}
      <Grid container>
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <ButtonCommon
            isLoadingPage={isLoadingButton}
            style={{ flex: 1, fontSize: 11, marginRight: 4, maxWidth: 160 }}
            variant="contained"
            color="green"
            onClick={openCreateModal}
          >
            Create New Offer
          </ButtonCommon>
        </Grid>
      </Grid>

      {/* Table Header */}
      <Grid container style={{ marginTop: 20 }}>
        <CardCommon backgroundColor="table_header_background">
          <Grid container className={classes.tableHeader}>
            <Grid item xs={3} className={classes.textLeft}>
              Name
            </Grid>
            <Grid item xs={3}>
              Description
            </Grid>
            <Grid item xs={3}>
              Offer Type
            </Grid>
            <Grid item xs={3} className={classes.textRight}>
              Status
            </Grid>
          </Grid>
        </CardCommon>
      </Grid>

      {/* Offer List */}
      <Grid container>
        {offers.length > 0 ? (
          offers.map((offer) => (
            <Grid item xs={12} key={offer.id} style={{ marginTop: 2 }}>
              <CardCommon
                backgroundColor="entity_background"
                className={classes.tableRow}
              >
                <Grid container alignItems="center">
                  <Grid item xs={3} className={classes.textLeft}>
                    {offer.name}
                  </Grid>
                  <Grid item xs={3}>
                    {offer.description}
                  </Grid>
                  <Grid item xs={3}>
                    {offerTypeMap[offer.offerType] || offer.offerType}
                  </Grid>
                  <Grid item xs={3} className={classes.textRight}>
                    <Typography style={{ paddingRight: 12 }}>
                      {offer.isActive ? "Active" : "Inactive"}
                    </Typography>
                    <ButtonCommon
                      style={{ fontSize: 11, minWidth: 28, height: 36 }}
                      variant="contained"
                      color="yellow"
                      onClick={() => openEditModal(offer)}
                    >
                      <EditIcon fontSize="small" />
                    </ButtonCommon>
                    <ButtonCommon
                      style={{
                        fontSize: 11,
                        minWidth: 28,
                        height: 36,
                        marginLeft: 12,
                      }}
                      variant="contained"
                      color="green"
                      isDisabled
                    >
                      <DeleteIcon fontSize="small" />
                    </ButtonCommon>
                  </Grid>
                </Grid>
              </CardCommon>
            </Grid>
          ))
        ) : (
          <Grid item xs={12} style={{ marginTop: 16 }}>
            <Typography variant="body2" align="center" color="textSecondary">
              No offers available.
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Create Offer Modal */}
      {isCreateModalOpen && (
        <CreateOfferModal
          openDialog={isCreateModalOpen}
          setOpenDialog={setIsCreateModalOpen}
          nodeData={{
            availability: [],
            isActive: true,
            minOrderPrice: 0,
            usageLimit: 0,
            deliveryTypes: [],
            priority: 50,
            platforms: [],
          }}
          getOfferInfo={getOfferInfo}
          offerItemMap={offerItemMap}
          setOfferItemMap={setOfferItemMap}
          offerName={""}
          
        />
      )}

      {/* Edit Offer Modal */}
      {isEditModalOpen && selectedOffer && (
        <EditOfferModal
          openDialog={isEditModalOpen}
          setOpenDialog={setIsEditModalOpen}
          nodeData={selectedOffer}
          getOfferInfo={getOfferInfo}
          offerItemMap={offerItemMap}
          setOfferItemMap={setOfferItemMap}
          offerName={selectedOffer.name}
        />
      )}
    </>
  );
};

export default WithLoading(OfferTypes);
